/**
 * Печатка і підпис організації (рішення власника 2026-08-13).
 *
 * Зберігаються через інфраструктуру вкладень (document_type='org.stamp' /
 * 'org.signature', document_id=орг): бек тримає PNG З ПРОЗОРІСТЮ (без
 * JPEG-конвеєра) — для накладання на документи у предпросмотрі вкладень.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { attachments } from '@/api/attachments';
import { useConfirm } from '@/components/ConfirmProvider';

export const STAMP_KINDS = [
  { type: 'org.stamp', label: 'Печатка підприємства' },
  { type: 'org.stamp_docs', label: 'Штамп для документів' },
  { type: 'org.signature', label: 'Підпис керівника' },
] as const;

function StampSlot({ orgId, type, label }: {
  orgId: string; type: string; label: string;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['attachments', type, orgId],
    queryFn: () => attachments.list({
      organization_id: orgId, document_type: type, document_id: orgId,
    }),
    enabled: !!orgId,
  });
  const current = listQ.data?.[listQ.data.length - 1];

  useEffect(() => {
    let alive = true;
    let url: string | null = null;
    if (current) {
      attachments.downloadBlob(current.id).then((b) => {
        if (!alive) return;
        url = URL.createObjectURL(b);
        setPreviewUrl(url);
      });
    } else {
      setPreviewUrl(null);
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [current?.id]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // Стара версія видаляється — тримаємо одну актуальну.
      if (current) await attachments.remove(current.id);
      return attachments.upload({
        organization_id: orgId, document_type: type,
        document_id: orgId, file,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', type, orgId] }),
    onError: (e) => {
      const d = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      window.alert(typeof d === 'string' ? d : 'Не вдалося завантажити зображення');
    },
  });
  const remove = useMutation({
    mutationFn: () => attachments.remove(current!.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments', type, orgId] }),
  });

  return (
    <div style={{
      border: '1px dashed var(--border, #cbd5e1)', borderRadius: 8,
      padding: '0.6rem', width: 190, textAlign: 'center',
    }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.4rem' }}>{label}</div>
      <div style={{
        height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'repeating-conic-gradient(#f1f5f9 0% 25%, #fff 0% 50%) 0 0 / 16px 16px',
        borderRadius: 6, marginBottom: '0.4rem',
      }}>
        {previewUrl
          ? <img src={previewUrl} alt={label} style={{ maxHeight: 84, maxWidth: '100%' }} />
          : <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>не завантажено</span>}
      </div>
      <div style={{ display: 'flex', gap: '0.3rem', justifyContent: 'center' }}>
        <button style={{ fontSize: '0.78rem' }} disabled={upload.isPending}
          onClick={() => fileRef.current?.click()}
          title="PDF до 2 МБ">
          {current ? 'замінити' : 'завантажити'}
        </button>
        {current && (
          <button className="danger" style={{ fontSize: '0.78rem' }}
            onClick={() => confirm(`Видалити ${label.toLowerCase()}?`)
              .then((ok) => { if (ok) remove.mutate(); })}>
            видалити
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" hidden
        accept=".pdf,application/pdf"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }} />
    </div>
  );
}

export default function OrgStamps({ orgId }: { orgId: string }) {
  if (!orgId) return null;
  return (
    <div style={{ marginTop: '1rem' }}>
      <h3 style={{ margin: '0 0 0.4rem', fontSize: '0.95rem' }}>Печатка і підпис</h3>
      {/* ч.1233 (рішення власника): без пояснень — лише формат і розмір файла;
          як це працює — стаття довідки «Печатка, штамп і підписи». */}
      <p style={{ color: 'var(--muted)', fontSize: '0.78rem', margin: '0 0 0.5rem' }}>PDF до 2 МБ</p>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        {STAMP_KINDS.map((k) => (
          <StampSlot key={k.type} orgId={orgId} type={k.type} label={k.label} />
        ))}
      </div>
    </div>
  );
}
